import React, { useState } from 'react';
import { winteamImportAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Upload, CheckCircle, Loader2, ChevronRight, Lock, Landmark } from 'lucide-react';
import { FileDrop, StatCard, HeaderReport, FlagTable, ReasonList } from '../components/import/ImportPrimitives';

export default function WinTeamBankingImportTab() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const runPreview = async () => {
    if (!file) { toast.error('Choose the NACHA transmission export'); return; }
    setParsing(true); setPreview(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await winteamImportAPI.bankingPreview(fd);
      setPreview(data);
      toast.success('Preview ready — nothing has been written yet');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setParsing(false);
    }
  };

  const confirm = async () => {
    setApplying(true);
    try {
      const { data } = await winteamImportAPI.bankingConfirm({ preview_id: preview.preview_id });
      setResult(data);
      setPreview(null);
      setFile(null);
      toast.success(`${data.accounts_added} accounts added · ${data.accounts_deactivated} deactivated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setApplying(false);
    }
  };

  const t = preview?.totals || {};

  return (
    <div className="space-y-6" data-testid="winteam-banking-import-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Landmark className="w-5 h-5" /> WinTeam Direct Deposit Import (NACHA)
          </CardTitle>
          <CardDescription>
            PAY Report → NACHA Bank Transmission. Banking setup only — deposit amounts, check numbers, check
            dates and addresses are ignored. One row per account, so employees with two accounts import both.
            Rows must match on EmployeeNumber <em>and</em> the last 4 of the SSN. A changed account deactivates
            the old one and adds a new record, and an active account missing from the file is deactivated.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-2 p-3 mb-4 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-600">
            <Lock className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
            <span>
              Account and routing numbers are encrypted before they are written. This preview and every screen
              in the app show only the last 4 digits; the full number requires the audited reveal.
            </span>
          </div>
          <FileDrop
            label="NACHA Bank Transmission export"
            hint="Click to choose .csv or .xlsx"
            accept=".csv,.xlsx"
            file={file}
            onFile={(f) => { setFile(f); setPreview(null); setResult(null); }}
            testId="winteam-banking-file-drop"
          />
          <div className="mt-4 flex items-center gap-3">
            <Button onClick={runPreview} disabled={parsing || !file} data-testid="winteam-banking-preview-btn">
              {parsing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Parsing…</> : 'Run Dry-Run Preview'}
            </Button>
            {preview && <span className="text-xs text-slate-500">Preview cached for 30 minutes.</span>}
          </div>
        </CardContent>
      </Card>

      {result && (
        <Alert className="border-emerald-300 bg-emerald-50" data-testid="winteam-banking-apply-result">
          <CheckCircle className="h-4 w-4 text-emerald-600" />
          <AlertDescription>
            <div className="font-semibold text-emerald-800">Direct deposit import complete</div>
            <div className="text-sm text-emerald-700 mt-1">
              {result.accounts_added} accounts added · {result.accounts_deactivated} deactivated
            </div>
          </AlertDescription>
        </Alert>
      )}

      {preview && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Accounts in file" value={t.accounts_in_file} testId="stat-accounts" />
            <StatCard label="Will add" value={t.will_add} tone="text-emerald-600" testId="stat-add" />
            <StatCard label="Already on file" value={t.unchanged} testId="stat-unchanged" />
            <StatCard label="Will deactivate" value={t.will_deactivate} tone="text-rose-600" testId="stat-deactivate" />
            <StatCard label="Rejected / skipped" value={(t.rejected || 0) + (t.skipped || 0)} tone="text-amber-600" testId="stat-rejected" />
          </div>

          {t.multi_account_employees > 0 && (
            <Alert data-testid="winteam-banking-multi-account">
              <AlertDescription className="text-sm">
                <span className="font-medium">{t.multi_account_employees}</span> employee(s) have more than one
                deposit account in this file: {preview.multi_account_employees.join(', ')}
              </AlertDescription>
            </Alert>
          )}

          <HeaderReport report={preview.header_report} />

          <Tabs defaultValue="add" className="space-y-4">
            <TabsList>
              <TabsTrigger value="add" data-testid="wt-bank-tab-add">
                Will add <Badge className="ml-2 bg-emerald-100 text-emerald-800">{t.will_add || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger value="unchanged" data-testid="wt-bank-tab-unchanged">
                Already on file <Badge className="ml-2 bg-slate-200 text-slate-700">{t.unchanged || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger value="deactivate" data-testid="wt-bank-tab-deactivate">
                Deactivate <Badge className="ml-2 bg-rose-100 text-rose-800">{t.will_deactivate || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger value="rejected" data-testid="wt-bank-tab-rejected">
                Rejected <Badge className="ml-2 bg-amber-100 text-amber-800">{(t.rejected || 0) + (t.skipped || 0)}</Badge>
              </TabsTrigger>
              <TabsTrigger value="flags" data-testid="wt-bank-tab-flags">
                Unused columns with data <Badge className="ml-2 bg-slate-200 text-slate-700">{t.flagged_columns || 0}</Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="add"><AccountRows rows={preview.will_add} /></TabsContent>
            <TabsContent value="unchanged"><AccountRows rows={preview.unchanged} /></TabsContent>
            <TabsContent value="deactivate"><DeactivateRows rows={preview.will_deactivate} /></TabsContent>
            <TabsContent value="rejected">
              <ReasonList
                rows={[...(preview.rejected || []), ...(preview.skipped || [])]}
                emptyText="Every row matched an employee cleanly."
                testId="wt-bank-rejected-list"
              />
            </TabsContent>
            <TabsContent value="flags"><FlagTable flags={preview.flags} /></TabsContent>
          </Tabs>

          <div className="flex items-center justify-end gap-3">
            <Button variant="outline" onClick={() => setPreview(null)} data-testid="wt-bank-discard-btn">Discard preview</Button>
            <Button onClick={confirm} disabled={applying} data-testid="winteam-banking-confirm-btn">
              {applying ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Applying…</> : <>Confirm import <ChevronRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function AccountRows({ rows }) {
  if (!rows?.length) return <p className="text-sm text-slate-400 py-6 text-center">No accounts in this bucket.</p>;
  const shown = rows.slice(0, 500);
  return (
    <Card><CardContent className="p-0">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Emp #</TableHead>
          <TableHead>Employee</TableHead>
          <TableHead>Bank</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Routing</TableHead>
          <TableHead>Account</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {shown.map((r, i) => (
            <TableRow key={`${r.employee_number}-${r.account_last4}-${i}`} data-testid={`wt-bank-row-${r.employee_number}-${r.account_last4}`}>
              <TableCell className="font-mono text-xs">{r.employee_number}</TableCell>
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell className="text-sm">
                {r.bank_name}
                {!r.bank_resolved && <Badge variant="outline" className="ml-2 text-xs">unresolved</Badge>}
                {!r.routing_valid && <Badge variant="destructive" className="ml-2 text-xs">bad checksum</Badge>}
              </TableCell>
              <TableCell className="text-sm">{r.account_type}</TableCell>
              <TableCell className="font-mono text-xs">••••{r.routing_last4}</TableCell>
              <TableCell className="font-mono text-xs">••••{r.account_last4}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > 500 && (
        <p className="p-3 text-xs text-slate-500">Showing the first 500 of {rows.length} accounts.</p>
      )}
    </CardContent></Card>
  );
}

function DeactivateRows({ rows }) {
  if (!rows?.length) return <p className="text-sm text-slate-400 py-6 text-center">No existing accounts are missing from the file.</p>;
  return (
    <Card><CardContent className="p-4 space-y-2">
      <p className="text-sm text-slate-600 mb-2">
        These accounts are on file in TriCorps360 but not in this transmission. They will be marked inactive
        and kept for the audit trail.
      </p>
      {rows.slice(0, 500).map((r) => (
        <div key={r.id} className="flex items-center gap-2 p-2 bg-rose-50 border border-rose-200 rounded-md text-sm" data-testid={`wt-bank-deactivate-${r.id}`}>
          <Landmark className="w-4 h-4 text-rose-500" />
          <span className="font-medium">{r.bank_name || 'Bank'}</span>
          <span className="text-slate-500">{r.account_type}</span>
          <span className="font-mono text-xs">••••{r.account_last4}</span>
        </div>
      ))}
      {rows.length > 500 && (
        <p className="text-xs text-slate-500">Showing the first 500 of {rows.length} accounts.</p>
      )}
    </CardContent></Card>
  );
}
